"use client"

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable @next/next/no-img-element */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react"
import ReactMarkdown, { Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import rehypeKatex from "rehype-katex"
import rehypeSlug from "rehype-slug"
import rehypeAutolinkHeadings from "rehype-autolink-headings"
import remarkDirective from "remark-directive"
import { remarkAdmonition } from "@/lib/remark-admonition"
import { Admonition } from "@/components/markdown/Admonition"
import { cn } from "@/lib/utils"
import ImageWithSkeleton from "@/components/post/ImageWithSkeleton"
import EyecatchImage from "@/components/post/EyecatchImage"
import CodeBlock from "@/components/post/CodeBlock"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import Link from "next/link"

import { Heart, Undo2 } from "lucide-react" // Undo2アイコンを追加インポート
import { trpc } from "@/trpc/react"
import { useToast } from "@/hooks/use-toast"
import { Skeleton } from "@/components/ui/skeleton"

interface NotionPostProps {
  metadata: {
    id: string
    title: string
    description: string
    slug: string
    date: string | null
    tags: string[]
    isImportant: boolean
    isSubscribed: boolean
    noticeCategory: string
    studyCategory: string
    amazonUrl: string
    rakutenUrl: string
  }
  markdown: string
  initialPic: string
}

// カスタムコンポーネントの型を定義
interface CustomComponents extends Components {
  Admonition?: React.ComponentType<any>
}

const NotionPostRenderer = ({
  metadata,
  markdown,
  initialPic,
}: NotionPostProps) => {
  const { toast } = useToast()

  // お気に入りステータス取得
  const favoriteQuery = trpc.book.getBookFavoriteStatus.useQuery(
    { bookId: metadata.id },
    {
      enabled: !!metadata.id,
      staleTime: 5000,
    },
  )

  // お気に入りトグル用ミューテーション
  const toggleMutation = trpc.book.toggleFavorite.useMutation({
    onError: (error) => {
      if (error.data?.code === "UNAUTHORIZED") {
        toast({
          title: "ログインが必要です",
          description: "お気に入りに追加するにはログインしてください。",
          variant: "destructive",
        })
      } else {
        toast({
          title: "エラーが発生しました",
          description: "再読み込みをお試しください。",
          variant: "destructive",
        })
        console.error(error)
      }
    },
  })

  const handleToggleFavorite = async () => {
    if (!metadata.id) return
    await toggleMutation.mutateAsync({ bookId: metadata.id })
    favoriteQuery.refetch()
  }

  const isFavorited = favoriteQuery.data?.isFavorited ?? false
  const favoritesCount = favoriteQuery.data?.count ?? 0

  // ローディング中表示
  const isLoadingFavorite = favoriteQuery.isLoading

  // components オブジェクトを定義し、型を指定（変更しない）
  const components: CustomComponents = {
    h1: ({ node, ...props }) => <h1 {...props} />,
    h2: ({ node, ...props }) => <h2 {...props} />,
    h3: ({ node, ...props }) => <h3 {...props} />,
    p: ({ node, ...props }) => <p {...props} />,
    ul: ({ node, ...props }) => <ul {...props} />,
    ol: ({ node, ...props }) => <ol {...props} />,
    li: ({ node, ...props }) => <li {...props} />,
    table: ({ node, ...props }) => (
      <table className="table-auto w-full" {...props} />
    ),
    thead: ({ node, ...props }) => <thead {...props} />,
    tbody: ({ node, ...props }) => <tbody {...props} />,
    tr: ({ node, ...props }) => <tr {...props} />,
    th: ({ node, ...props }) => <th className="px-4 py-2" {...props} />,
    td: ({ node, ...props }) => <td className="border px-4 py-2" {...props} />,
    code: ({ node, className, children, ...props }) => (
      <CodeBlock className={className}>{children}</CodeBlock>
    ),
    // カスタムタグ名を小文字で指定
    Admonition: ({ node, ...props }) => {
      const type = props.type || "note"
      return (
        <Admonition type={type as any} {...props}>
          {props.children}
        </Admonition>
      )
    },
    img: ({ src, alt, ...props }) => (
      <ImageWithSkeleton
        src={src || initialPic}
        alt={alt || "initial image"}
        initialPic={initialPic}
        {...props}
      />
    ),
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 書籍一覧に戻るボタン */}
      <div className="mb-6">
        <Link href="/book">
          <Button variant="link" className="flex items-center pl-0">
            <Undo2 className="mr-2 h-6 w-6" />
            書籍一覧に戻る
          </Button>
        </Link>
      </div>

      {/* カードレイアウト: ゆとりをもたせるために gap-4, p-4 などを追加 */}
      <Card className="flex flex-col md:flex-row gap-4 overflow-hidden shadow-md hover:shadow-lg transition-shadow mb-6 p-4">
        {/* アイキャッチ画像部分（EyecatchImage コンポーネントを使用） */}
        <div className="relative w-full md:w-1/2 aspect-auto overflow-hidden rounded-md border border-muted">
          <EyecatchImage id={metadata.id} initialPic={initialPic} />
        </div>

        {/* 右側のメタ情報部分 */}
        <div className="md:w-1/2 flex flex-col justify-between">
          <CardContent className="flex-grow space-y-4">
            {/* タグと日付 */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {metadata.date && (
                <time dateTime={metadata.date} className="mr-2">
                  {metadata.date}
                </time>
              )}
              {metadata.tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="default"
                  className="px-2 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  #{tag}
                </Badge>
              ))}
            </div>

            {/* タイトル */}
            <h2 className="text-xl font-semibold leading-tight">
              {metadata.title}
            </h2>

            {/* 説明: line-clamp を削除して全文表示 */}
            <p className="text-sm text-muted-foreground">
              {metadata.description}
            </p>
          </CardContent>

          {/* ボタン群 + お気に入り */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-2 mt-4 gap-4">
            <div className="flex gap-2">
              <Button
                asChild
                variant="outline"
                className="bg-[#FF9900] text-white hover:bg-[#FF9900]/90 max-h-[40px]"
                size="sm"
              >
                <Link href={metadata.amazonUrl}>Amazon</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-[#BF0000] text-white hover:bg-[#BF0000]/90 max-h-[40px]"
                size="sm"
              >
                <Link href={metadata.rakutenUrl}>Rakuten</Link>
              </Button>
            </div>

            {/* お気に入り（ハート） */}
            {isLoadingFavorite ? (
              <Skeleton className="w-20 h-6" />
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleToggleFavorite}
                  className="text-muted-foreground hover:text-red-500"
                >
                  {isFavorited ? (
                    <Heart className="h-5 w-5 fill-current text-red-500" />
                  ) : (
                    <Heart className="h-5 w-5" />
                  )}
                  <span className="sr-only">いいね</span>
                </Button>
                <span className="text-xs">
                  {favoritesCount === 0
                    ? "0人がオススメ"
                    : `${favoritesCount}人がオススメ`}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* 本文（Markdown） */}
      <article className="prose w-full mt-8">
        <ReactMarkdown
          remarkPlugins={[
            remarkGfm,
            remarkMath,
            remarkDirective,
            remarkAdmonition,
          ]}
          rehypePlugins={[
            rehypeKatex,
            rehypeSlug,
            [
              rehypeAutolinkHeadings,
              {
                behavior: "append",
                properties: { className: "header-anchor" },
              },
            ],
          ]}
          components={components}
        >
          {markdown}
        </ReactMarkdown>
      </article>
    </div>
  )
}

export default NotionPostRenderer
